import { Box, Grid, InputAdornment } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

/** API */
import ManageDoctorFeeApi, { DoctorFeeInterface, FindManageDoctorAllInterface } from 'api/counter/manage-doctor-fee.api'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** UTILS || SLICE */
import { resetModal, showModal } from 'app/slice/modal.slice'
import { numberFormat, toBuddhistYear } from 'utils/app.utils'
import { isCreate, isDelete, isExport, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'

/** STYLE */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MainButton from 'new-components/buttons/main-button'
import TextInput from 'new-components/inputs/text-input'
import FromDoctorFee from './from-doctor-fee'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView(),
  isExport: isExport()
}

export default function ManageDoctorFee() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [doctorFees, setDoctorFees] = useState<DoctorFeeInterface[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [dataModal, setDataModal] = useState<{ isShow: boolean; data: any }>({ isShow: false, data: {} })

  /** pagination */
  const [page, setPage] = useState<number>(1)
  const [pageLimit, setPageLimit] = useState<number>(10)
  const [rowCount, setRowCount] = useState<number>(0)
  const [search, setSearch] = useState<string>('')
  const [rangeDate, setRangeDate] = useState<string[]>([moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')])

  const loadDoctorFees = async () => {
    setLoading(true)
    let condition: FindManageDoctorAllInterface = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (rangeDate[0] !== null && rangeDate[0] !== '' && rangeDate[1] !== null && rangeDate[1] !== '') {
      condition = { ...condition, dateStart: rangeDate[0], dateEnd: rangeDate[1] }
    }
    await ManageDoctorFeeApi.findAll(condition)
      .then(({ data, headers }) => {
        setDoctorFees(data)
        setRowCount(Number(headers['x-total']))
      })
      .catch(() => setDoctorFees([]))
      .finally(() => {
        setLoading(false)
      })

  }

  useEffect(() => {
    loadDoctorFees()
  }, [page, pageLimit])

  const headCells = [
    { id: 'id', disablePadding: false, label: t('TABLE.NO'), align: 'center', width: '70px' },
    { id: 'date', disablePadding: false, label: t('MANAGE_DOCTOR_FEE.TABLE.CELLS.RECEIPT_DATE') },
    { id: 'paymentNumber', disablePadding: false, label: t('MANAGE_DOCTOR_FEE.TABLE.CELLS.RECEIPT_NO') },
    { id: 'branchCnNumber', disablePadding: false, label: t('MANAGE_DOCTOR_FEE.TABLE.CELLS.PATIENT_CNCODE') },
    { id: 'patientsFullname', disablePadding: false, label: t('MANAGE_DOCTOR_FEE.TABLE.CELLS.PATIENT_NAME') },
    { id: 'by', disablePadding: false, label: t('MANAGE_DOCTOR_FEE.TABLE.CELLS.PAYMENT_NAME') },
    { id: 'doctorFullname', disablePadding: false, label: t('MANAGE_DOCTOR_FEE.TABLE.CELLS.DOCTOR_ORDERER') },
    { id: 'operativeName', disablePadding: false, label: t('MANAGE_DOCTOR_FEE.TABLE.CELLS.OPERATIVE_LIST') },
    { id: 'total', align: 'right', disablePadding: false, label: t('MANAGE_DOCTOR_FEE.TABLE.CELLS.OPERATIVE_PRICE') },
    { id: 'cfAmountNet', align: 'right', disablePadding: false, label: t('MANAGE_DOCTOR_FEE.TABLE.CELLS.CLINIC_FREE') },
    { id: 'dfAmountNet', align: 'right', disablePadding: false, label: t('MANAGE_DOCTOR_FEE.TABLE.CELLS.DF') },
    { id: 'updatedAt', align: 'center', disablePadding: false, label: t('MANAGE_DOCTOR_FEE.TABLE.CELLS.UPDATEDAT') },
    { id: 'action', disablePadding: false, label: t('Action') }
  ]

  const onEdit = (objData: any) => {
    setDataModal({ isShow: true, data: objData })
    dispatch(showModal())
  }

  const renderData = (objData: DoctorFeeInterface, no: number, exportDataValue: boolean) => {
    const { orderTreatmentId, date, paymentNumber, doctorFullname, branchCode, branchCnNumber, patientsFullname, clinicFeeId, operativeName, dfUnit, cfAmount, total, clinicFeePrice, dfAmount, dfAmountNet, updatedBy, updatedAt, createdBy } = objData
    const cf = clinicFeeId ? total - clinicFeePrice : total
    const clinicFree = dfUnit === 'PERCENT' ? cf * (dfAmount / 100) : dfAmount
    no = page * pageLimit - pageLimit + no + 1

    const objRenderData = {
      key: String(Math.random()),
      id: String(orderTreatmentId),
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no, class: 'px-1' },
        { option: 'TEXT', align: 'left', label: date ? toBuddhistYear(moment(date)) : '-' },
        { option: 'TEXT', align: 'left', label: paymentNumber },
        { option: 'TEXT', align: 'left', label: `${branchCode}${branchCnNumber}` },
        { option: 'TEXT', align: 'left', label: patientsFullname },
        { option: 'TEXT', align: 'left', label: createdBy },
        { option: 'TEXT', align: 'left', label: doctorFullname },
        { option: 'TEXT', align: 'left', label: operativeName },
        { option: 'TEXT', align: 'right', label: numberFormat(total) },
        { option: 'TEXT', align: 'right', label: numberFormat(clinicFeePrice) },
        { option: 'TEXT', align: 'right', label: numberFormat(dfAmountNet) },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },

        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [{ option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled }]
        }
      ]
    }

    return <TableRowCommon {...objRenderData} onedit={() => onEdit(objData)} />
  }

  const onRequestSort = () => {
    setPage(1)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
    setPage(1)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  return (
    <Box sx={{ height: '100%' }}>
      <HeaderCard text={t('MANAGE_DOCTOR_FEE.HEADER')} />
      <Box sx={{ px: 3, pt: 2 }}>
        <Grid container spacing={2} >

          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('MANAGE_DOCTOR_FEE.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => setSearch('')}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <InputSecondNewRangePicker
              inputHeight={40}
              value={rangeDate || []}
              onchange={(val) => {
                if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
              }}
              allowClear
              onClear={() => setRangeDate(emptyRangePicker)}
            />
          </Grid>
          <Grid item xs={12} md={1} xl={1}>
            <MainButton
              title={t('INPUT.SEARCH')}
              type="primary"
              variant="contained"
              onClick={() => {
                setPage(1)
                loadDoctorFees()
              }}
              style={{ width: '120px' }}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, px: { xs: 1 }, pb: 4 }}>
          <TableCustom
            tableFixedWidth
            tableMinWidth={2500}
            page={page}
            pageLimit={pageLimit}
            sortType={'ASC'}
            sortBy={'updatedAt'}
            rowCount={rowCount}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            headCells={headCells}
            rowsData={_.map(doctorFees, renderData)}
          />
        </Box>
        {dataModal.isShow && (
          <FromDoctorFee
            type="CLINIC_FEE"
            data={dataModal.data}
            onReset={(e) => {
              setDataModal({
                isShow: e,
                data: []
              })
              dispatch(resetModal())
              loadDoctorFees()
            }}
          />
        )}
      </Box>
      <Loading show={loading} type={'fullLoading'} />
    </Box>
  )
}
